import { useCallback, useEffect, useState } from "react";
import "./WebPushPermissionBanner.css";

import {
  WEB_PUSH_LOGIN_PROMPT_KEY,
  WEB_PUSH_BANNER_DISMISSED_KEY,
} from "./webPushKeys";

// Re-exported for back-compat with existing importers (Layout, LoginPage, tests).
export { WEB_PUSH_LOGIN_PROMPT_KEY, WEB_PUSH_BANNER_DISMISSED_KEY };

// Browsers do NOT let a web page navigate to chrome://settings (or open the
// permission prompt again once a site is blocked), so a true one-click "open
// settings" button is impossible. The closest we can offer is the exact
// per-site settings deep link, which the user CAN reach by pasting it into the
// address bar. buildSiteSettingsUrl() returns that link for the current
// browser family (Chrome / Brave / Edge / Opera), or null when we can't map it.
function buildSiteSettingsUrl(origin) {
  if (!origin) return null;
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const site = encodeURIComponent(origin);
  if (/Edg\//i.test(ua)) {
    return `edge://settings/content/siteDetails?site=${site}`;
  }
  if (/OPR\//i.test(ua)) {
    return `opera://settings/content/siteDetails?site=${site}`;
  }
  // Chrome, Brave, and other Chromium browsers accept the chrome:// form.
  if (/Chrome\//i.test(ua)) {
    return `chrome://settings/content/siteDetails?site=${site}`;
  }
  return null;
}

function readPermission() {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "unsupported";
  }
  return Notification.permission;
}

// GMA-1429: a granted browser permission is NOT sufficient — the OS (macOS /
// Windows) can still block Chrome from showing notifications, silently hiding
// life-safety fall alerts. Surface the exact OS-level fix right where we claim
// alerts are "enabled" so caregivers aren't left wondering why nothing pops.
function osNotificationHint() {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  if (/Macintosh|Mac OS X/i.test(ua)) {
    return (
      "Not seeing alerts? macOS must also allow Chrome: System Settings → " +
      "Notifications → Google Chrome → turn on Allow notifications " +
      "(Banners or Alerts), and turn off Focus / Do Not Disturb. If Chrome " +
      "isn't listed there, it hasn't been allowed at the OS level yet."
    );
  }
  if (/Windows/i.test(ua)) {
    return (
      "Not seeing alerts? Windows must also allow Chrome: Settings → " +
      "System → Notifications → Google Chrome → On, and turn off " +
      "Focus assist / Do Not Disturb."
    );
  }
  return (
    "Not seeing alerts? Your operating system must allow Google Chrome to show " +
    "notifications, and Do Not Disturb must be off."
  );
}

/**
 * GMA-1318 — post-login prompt for Chrome desktop web push.
 * Chrome only shows Notification.requestPermission after a user gesture;
 * this banner gives that gesture right after login instead of hiding it on the bell.
 */
export default function WebPushPermissionBanner({
  visible = false,
  isSubscribed = false,
  onEnable,
  onDismiss,
}) {
  const [permission, setPermission] = useState(readPermission);
  const [settingsLinkCopied, setSettingsLinkCopied] = useState(false);

  useEffect(() => {
    setPermission(readPermission());
    setSettingsLinkCopied(false);
  }, [visible]);

  const handleReload = useCallback(() => {
    if (typeof window !== "undefined" && window.location) {
      window.location.reload();
    }
  }, []);

  const handleCopySettingsLink = useCallback(async (url) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setSettingsLinkCopied(true);
      }
    } catch (_err) {
      // Clipboard can be blocked; the link is always rendered inline below as a
      // manual-copy fallback, so the user can still reach it.
    }
  }, []);

  const handleEnable = useCallback(async () => {
    const ok = await onEnable?.();
    setPermission(readPermission());
    if (ok) {
      try {
        sessionStorage.removeItem(WEB_PUSH_LOGIN_PROMPT_KEY);
      } catch (_err) {
        // ignore
      }
    }
  }, [onEnable]);

  const handleDismiss = useCallback(() => {
    try {
      sessionStorage.removeItem(WEB_PUSH_LOGIN_PROMPT_KEY);
      sessionStorage.setItem(WEB_PUSH_BANNER_DISMISSED_KEY, "1");
    } catch (_err) {
      // ignore
    }
    onDismiss?.();
  }, [onDismiss]);

  if (!visible) return null;

  // GMA-1318 (Copilot #201): use the actual host so the "open site settings"
  // hint is correct across prod / staging / localhost, not a baked-in domain.
  const siteHost =
    typeof window !== "undefined" && window.location
      ? window.location.host
      : "this site";

  // Permission alone is NOT enough — a live backend subscription must be
  // confirmed. A granted permission whose subscribe silently failed (stale
  // sub, VAPID-key change, save error) would otherwise show a reassuring
  // "enabled" while delivering zero pushes. Only report "enabled" when the
  // subscription is actually confirmed; otherwise prompt a re-enable.
  if (permission === "granted" && isSubscribed) {
    return (
      <div
        className="web-push-permission-banner web-push-permission-banner--success"
        role="status"
      >
        Desktop fall alerts are enabled for this browser.
        <p className="web-push-permission-banner__os-hint">
          {osNotificationHint()}
        </p>
        <button type="button" className="web-push-banner-btn" onClick={handleDismiss}>
          Dismiss
        </button>
      </div>
    );
  }

  if (permission === "granted" && !isSubscribed) {
    return (
      <div
        className="web-push-permission-banner web-push-permission-banner--warning"
        role="alert"
      >
        Desktop fall alerts aren&apos;t fully set up on this browser yet — you
        won&apos;t receive them until you re-enable.
        <div className="web-push-permission-banner__actions">
          <button
            type="button"
            className="web-push-banner-btn web-push-banner-btn--primary"
            onClick={handleEnable}
          >
            Re-enable desktop alerts
          </button>
          <button type="button" className="web-push-banner-btn" onClick={handleDismiss}>
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  if (permission === "denied") {
    const settingsUrl = buildSiteSettingsUrl(
      typeof window !== "undefined" && window.location
        ? window.location.origin
        : null,
    );
    return (
      <div
        className="web-push-permission-banner web-push-permission-banner--denied"
        role="alert"
      >
        <p className="web-push-permission-banner__lead">
          Notifications are blocked for CompassCare. To turn them back on:
        </p>
        <ol className="web-push-permission-banner__steps">
          <li>
            Click the site-settings icon (the sliders or lock) at the left end
            of the address bar, next to <strong>{siteHost}</strong>.
          </li>
          <li>
            Set <strong>Notifications</strong> to <strong>Allow</strong>.
          </li>
          <li>
            Come back here and click <strong>Reload</strong> below.
          </li>
        </ol>
        {settingsLinkCopied && settingsUrl && (
          <p className="web-push-permission-banner__copied" role="status">
            Settings link copied — paste it into your address bar and press
            Enter to jump straight to this site&apos;s notification setting.
          </p>
        )}
        <div className="web-push-permission-banner__actions">
          {settingsUrl && (
            <button
              type="button"
              className="web-push-banner-btn web-push-banner-btn--primary"
              onClick={() => handleCopySettingsLink(settingsUrl)}
            >
              Copy settings link
            </button>
          )}
          <button type="button" className="web-push-banner-btn" onClick={handleReload}>
            Reload
          </button>
          <button type="button" className="web-push-banner-btn" onClick={handleDismiss}>
            Dismiss
          </button>
        </div>
        {settingsUrl && (
          <p className="web-push-permission-banner__link">
            Or open this in your address bar:{" "}
            <code className="web-push-permission-banner__url">{settingsUrl}</code>
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="web-push-permission-banner" role="region" aria-label="Enable desktop fall alerts">
      <p>
        Allow desktop fall alerts so tier-1 Pontosense events show an OS banner
        and sound when this tab is in the background.
      </p>
      <div className="web-push-permission-banner__actions">
        <button
          type="button"
          className="web-push-banner-btn web-push-banner-btn--primary"
          onClick={handleEnable}
        >
          Allow desktop alerts
        </button>
        <button type="button" className="web-push-banner-btn" onClick={handleDismiss}>
          Not now
        </button>
      </div>
    </div>
  );
}
